// src/string.ts
function truncate(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function capitalize(text) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
function truncateForMeta(text, maxLength = 160) {
  if (!text) return "";
  const codePoints = [...text];
  if (codePoints.length <= maxLength) return text;
  const budget = Math.max(0, maxLength - 1);
  let sliced = codePoints.slice(0, budget).join("");
  const lastSpace = sliced.lastIndexOf(" ");
  if (lastSpace > 0) sliced = sliced.slice(0, lastSpace);
  return `${sliced.trimEnd()}\u2026`;
}
function stripHtml(html) {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/\s+/g, " ").trim();
}
function slugify(text, maxLength = 50) {
  if (!text) return "";
  const COMBINING_MARKS = /[̀-ͯ]/g;
  return text.toLowerCase().normalize("NFD").replace(COMBINING_MARKS, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, maxLength);
}
function plural(count, singular, pluralForm) {
  const n = Math.abs(count);
  if (n < 2) return singular;
  return pluralForm ?? `${singular}s`;
}
function pluralCount(count, singular, pluralForm) {
  return `${count} ${plural(count, singular, pluralForm)}`;
}
var RNA_ENCODING_FIXES = [
  // Formes doublement encodées d'abord : elles contiennent « Ã », que les
  // règles suivantes ne doivent pas voir.
  [/ÃŽ/g, "\xF4"],
  [/Ã©/g, "\xE9"],
  [/Ã¨/g, "\xE8"],
  [/Ãª/g, "\xEA"],
  [/Ã´/g, "\xF4"],
  [/Ã§/g, "\xE7"],
  [/Ã®/g, "\xEE"],
  [/Ã¹/g, "\xF9"],
  [/Ã»/g, "\xFB"],
  [/Ã\s{1,2}/g, "\xE0 "],
  [/â€™/g, "\u2019"],
  // Apostrophe : « là’orgue » → « l'orgue ». Seulement après une lettre, pour
  // ne pas toucher un « à » en fin de mot suivi d'une vraie apostrophe.
  [/([a-zA-ZÀ-ÿ])à’/g, "$1'"],
  [/àœ/g, "\u0153"],
  // « è » : entre deux majuscules on reste en capitales (« DÉCÈS »).
  [/(?<=[A-ZÀ-Ý])Ú(?=[A-ZÀ-Ý])/g, "\xC8"],
  [/Ú/g, "\xE8"],
  // « ô » : uniquement en milieu de mot minuscule — « Île », « Îlot » restent.
  [/(?<=[a-zà-ÿ])Î/g, "\xF4"],
  // « à » suivi de deux espaces ou plus.
  [/à {2,}/g, "\xE0 "]
];
var RNA_ENCODING_MARKERS = /Ú|Î|Ã|à’|à {2}|àœ|â€/;
function fixRnaEncoding(text) {
  if (!text || !RNA_ENCODING_MARKERS.test(text)) return text;
  let out = text;
  for (const [pattern, replacement] of RNA_ENCODING_FIXES) {
    out = out.replace(pattern, replacement);
  }
  return out;
}
function replierPourRecherche(text) {
  if (!text) return "";
  const COMBINING_MARKS = /[\u0300-\u036f]/g;
  return text.toLowerCase().normalize("NFD").replace(COMBINING_MARKS, "");
}
function correspondALaRecherche(recherche, ...champs) {
  const terme = replierPourRecherche(recherche?.trim());
  if (!terme) return true;
  return champs.some((c) => replierPourRecherche(c).includes(terme));
}
var EXTENSIONS_NUES = "com|org|net|fr|eu|be|ch|lu|ma|dz|tn|sn|ci|re|gp|mq|gf|yt|pm|nc|pf|io|app|info|co|me|uk|de|es|it|nl";
var MOTIF_LIENS = new RegExp(
  `(https?://[^\\s<>"]+)|([A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)*\\.[A-Za-z]{2,})|(www\\.[^\\s<>"]+)|((?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\\.)+(?:${EXTENSIONS_NUES})(?![A-Za-z0-9-])(?:/[^\\s<>"]*)?)`,
  "gi"
);
var FIN_PARASITE = `.,;:!?'"\xBB)]}\u2026`;
function sansPonctuationFinale(lien) {
  let l = lien;
  while (l.length > 0 && FIN_PARASITE.includes(l[l.length - 1])) {
    if (l.endsWith(")") && l.split("(").length > l.split(")").length - 1) break;
    l = l.slice(0, -1);
  }
  return l;
}
function decouperLiens(texte) {
  if (!texte) return [];
  const morceaux = [];
  let curseur = 0;
  for (const m of texte.matchAll(MOTIF_LIENS)) {
    const debut = m.index ?? 0;
    const avant = debut > 0 ? texte[debut - 1] : "";
    if (avant && /[A-Za-z0-9@./-]/.test(avant)) continue;
    const brut = sansPonctuationFinale(m[0]);
    if (!brut) continue;
    const url = m[1] ? brut : m[2] ? `mailto:${brut}` : `https://${brut}`;
    if (debut > curseur) morceaux.push({ texte: texte.slice(curseur, debut) });
    morceaux.push({ texte: brut, url });
    curseur = debut + brut.length;
  }
  if (curseur < texte.length) morceaux.push({ texte: texte.slice(curseur) });
  return morceaux;
}

export { capitalize, correspondALaRecherche, decouperLiens, fixRnaEncoding, plural, pluralCount, replierPourRecherche, slugify, stripHtml, truncate, truncateForMeta };
//# sourceMappingURL=string.js.map
//# sourceMappingURL=string.js.map